import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { attachmentEntityType } from "../../../variables/Enums";
import { getIsfFiles, uploadAttachmentMetadata } from "../../../api/clinicalTrial";
import { Spinner } from "../../../components/Utils/Loaders";
import FileManagerISF from "./FileManagerISF";
import { CustomSwitch } from "../../../components/CustomControls/CustomSwitch";
import ListFileISF from "./ListFileISF";
import { Edit } from "../../../components/Icons/Icons";

/*
 * Subtabs ISF (Documentación / Filtros) comentados: la vista es solo documentación.
 * Contenido previo del subtab Filtros (ListFileISF + categoryFilter) en branch filters-isf:
 *
 * import { Tab, Tabs, FormControl } from "react-bootstrap";
 * import { useTranslation } from "react-i18next";
 * import { useLocation, useHistory } from "react-router-dom";
 *
 * const filtrosContent = (... FormControl select + ListFileISF categoryFilter ...);
 * <Tabs><Tab eventKey="documentacion">...</Tab><Tab eventKey="filtros">{filtrosContent}</Tab></Tabs>
 */

export const ISFFiles = ({
  startup,
  setModifiedCategories,
  setModifiedSubtitles,
  modifiedSubtitles,
  refreshKey,
}) => {
  const { id } = useParams();
  const [isfFiles, setIsfFiles] = useState();
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [viewMode, setViewMode] = useState("filemanager");
  const [isEditingSubtitles, setIsEditingSubtitles] = useState(false);

  useEffect(() => {
    if (!id) {
      return;
    }

    const fetchData = async () => {
      const result = await getIsfFiles(id);

      if (result.status === 200) {
        setIsfFiles(result.data);
      }
    };
    fetchData();
  }, [id, refreshKey]);

  /** Tras guardar subtítulos, el padre incrementa refreshKey: salir del modo edición y restaurar navegación (doble clic en carpetas). */
  useEffect(() => {
    setIsEditingSubtitles(false);
  }, [refreshKey]);

  const groupArrayByTwo = (arr) => {
    const result = [];
    for (let i = 0; i < arr.length; i += 2) {
      result.push(arr.slice(i, i + 2));
    }
    return result;
  };

  const handleCategoryNameChange = (systemId, newName) => {
    setModifiedCategories((prev) => {
      const exists = prev.find((item) => item.systemId === systemId);
      if (exists) {
        return prev.map((item) =>
          item.systemId === systemId ? { ...item, categoryName: newName } : item
        );
      } else {
        return [...prev, { systemId, categoryName: newName }];
      }
    });
  };

  const handleCategoryCreated = (created, currentPath) => {
    const toAttachmentItem = (created) => ({
      attachmentFileName: null,
      attachmentCategoryId: created.id,
      attachmentCategoryName: created.name,
      entityTypeId: created.attachmentEntityTypeId,
      multipleAttachments: !!created.multipleFiles,
      id: null,
      extendedMetadata: null,
      systemId: created.systemId,
    });

    const newItem = toAttachmentItem(created);

    setIsfFiles((prev) => {
      if (!prev) return prev;

      const newPathEntry = {
        id: created.systemId,
        attachmentCategoryId: created.systemId,
        path: currentPath,
        attachment: newItem,
      };

      switch (created.attachmentEntityTypeId) {
        case attachmentEntityType.Startup:
          newItem.entityId = prev.startupAttachments[0].entityId;
          return {
            ...prev,
            startupAttachments: [...(prev.startupAttachments || []), newItem],
            attachmentPaths: [...(prev.attachmentPaths || []), newPathEntry],
          };

        case attachmentEntityType.Practitioner:
          return {
            ...prev,
            practitioners: (prev.practitioners || []).map((p) => {
              newItem.entityId = p.attachments[0].entityId;
              return {
                ...p,
                attachments: [...(p.attachments || []), newItem],
              };
            }),
            attachmentPaths: [...(prev.attachmentPaths || []), newPathEntry],
          };

        case attachmentEntityType.Hospital:
          newItem.entityId = "global";
          return {
            ...prev,
            hospitalAttachments: [...(prev.hospitalAttachments || []), newItem],
            attachmentPaths: [...(prev.attachmentPaths || []), newPathEntry],
          };

        default:
          return {
            ...prev,
            attachmentPaths: [...(prev.attachmentPaths || []), newPathEntry],
          };
      }
    });
    setShowNewCategoryForm(false);
  };

  const handleAttachmentChange = async (entityTypeId, entityId, attachment) => {
    if (attachment?.id && attachment?.extendedMetadata) {
      try {
        await uploadAttachmentMetadata(
          entityTypeId,
          attachment.id,
          attachment.extendedMetadata
        );
      } catch (err) {
        console.error("Error al actualizar metadata:", err);
      }
    }

    setIsfFiles((prev) => {
      if (!prev) return prev;

      const normalizeEntityId = (typeId, rawEntityId) => {
        if (typeId === attachmentEntityType.Hospital) {
          return String(rawEntityId ?? "global");
        }
        return String(rawEntityId ?? "");
      };

      const targetEntityId = normalizeEntityId(entityTypeId, entityId);

      const match = (a) =>
        a &&
        a.entityTypeId === entityTypeId &&
        a.attachmentCategoryId === attachment.attachmentCategoryId &&
        normalizeEntityId(a.entityTypeId, a.entityId) === targetEntityId;

      const updateAttachment = (a) => {
        if (!match(a)) return a;
        return {
          ...a,
          ...attachment,
          practitionerName:
            attachment.practitionerName ??
            attachment.PractitionerName ??
            a.practitionerName ??
            a.PractitionerName,
        };
      };

      return {
        ...prev,
        startupAttachments: (prev.startupAttachments || []).map(
          updateAttachment
        ),
        hospitalAttachments: (prev.hospitalAttachments || []).map(
          updateAttachment
        ),
        regulatorySubmissions: (prev.regulatorySubmissions || []).map((rs) => ({
          ...rs,
          attachments: (rs.attachments || []).map(updateAttachment),
        })),
        practitioners: (prev.practitioners || []).map((p) => ({
          ...p,
          attachments: (p.attachments || []).map(updateAttachment),
        })),
        attachmentPaths: (prev.attachmentPaths || []).map((ap) => ({
          ...ap,
          attachment: updateAttachment(ap.attachment),
        })),
      };
    });
  };

  const documentacionContent = (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
          marginTop: 12,
          flexWrap: "wrap",
        }}
      >
        {viewMode === "filemanager" && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setIsEditingSubtitles(!isEditingSubtitles)}
          >
            <Edit style={{ marginRight: 4, verticalAlign: "middle" }} />
            Subtítulos
          </button>
        )}
        <CustomSwitch
          name="viewMode"
          value={viewMode === "filemanager"}
          onChange={(name, newValue) =>
            setViewMode(newValue ? "filemanager" : "list")
          }
          onText="Explorador"
          offText="Lista"
          bsSize="mini"
        />
      </div>
      {viewMode === "filemanager" && (
        <FileManagerISF
          entityId={id}
          serviceData={isfFiles}
          legend="Subir documento"
          protocolId={startup?.protocol?.id}
          handleCategoryCreated={handleCategoryCreated}
          onAttachmentChanged={handleAttachmentChange}
          isEditingSubtitles={isEditingSubtitles}
          modifiedSubtitles={modifiedSubtitles}
          setModifiedSubtitles={setModifiedSubtitles}
        />
      )}
      {viewMode === "list" && (
        <ListFileISF
          isfFiles={isfFiles}
          id={id}
          groupArrayByTwo={groupArrayByTwo}
          handleCategoryNameChange={handleCategoryNameChange}
          showNewCategoryForm={showNewCategoryForm}
          setShowNewCategoryForm={setShowNewCategoryForm}
          handleCategoryCreated={handleCategoryCreated}
          onAttachmentChanged={handleAttachmentChange}
          protocolId={startup?.protocol?.id}
        />
      )}
    </>
  );

  return (
    <>
      {!isfFiles && <Spinner />}
      {isfFiles && documentacionContent}
    </>
  );
};

export default ISFFiles;
